import React, { useMemo } from 'react';
import { Polyline, Marker } from '@react-google-maps/api';
import IconEnd from '../../assets/svg/maps/Icon_end.svg';
import IconStar from '../../assets/svg/maps/Icon_star.svg';
import PropTypes from 'prop-types';

const RouteRenderer = ({ 
  trip, 
  googleMapsPolyline, 
  actualPath = [],
  routeColor = '#4285F4', // Color por defecto (azul de Google)
  isSelected = false,
}) => {
  // Decodificar la ruta de Google Maps
  const decodedGooglePath = useMemo(() => {
    if (!googleMapsPolyline || !window.google?.maps?.geometry?.encoding) return [];
    try {
      return window.google.maps.geometry.encoding.decodePath(googleMapsPolyline);
    } catch (error) {
      console.error("Error decodificando polyline:", error);
      return [];
    }
  }, [googleMapsPolyline]);
  
  // Convertir puntos de ruta actual
  const actualLatLngPath = useMemo(() => {
    // Validar que actualPath sea un array y tenga elementos
    if (!Array.isArray(actualPath) || actualPath.length === 0) {
      console.debug("Sin datos de ruta actual para", trip?.vehiclePlate);
      return [];
    }
    
    // Filtrar y mapear los puntos con validación robusta
    return actualPath
      .filter(point => {
        // Verificar que el punto tiene coordenadas válidas
        const hasValidLat = point?.latitud !== undefined && point?.latitud !== null;
        const hasValidLng = point?.longitud !== undefined && point?.longitud !== null;
        return hasValidLat && hasValidLng;
      })
      .map(point => {
        // Convertir a valores numéricos
        const lat = parseFloat(point.latitud);
        const lng = parseFloat(point.longitud);
        
        // Verificar que son números válidos
        if (isNaN(lat) || isNaN(lng)) {
          console.warn("Coordenadas inválidas:", point);
          return null;
        }
        
        return { lat, lng };
      })
      .filter(Boolean); // Eliminar valores nulos
  }, [actualPath, trip?.vehiclePlate]);

  // Procesar puntos de origen y destino
  const originMarker = useMemo(() => {
    if (!trip?.origin?.lat || !trip?.origin?.lon) return null;
    try {
      return { 
        lat: parseFloat(trip.origin.lat), 
        lng: parseFloat(trip.origin.lon)
      };
    } catch (error) {
      console.error("Error en coordenadas de origen:", error);
      return null;
    }
  }, [trip?.origin]);

  const destinationMarker = useMemo(() => {
    if (!trip?.destination?.lat || !trip?.destination?.lon) return null;
    try {
      return { 
        lat: parseFloat(trip.destination.lat), 
        lng: parseFloat(trip.destination.lon)
      };
    } catch (error) {
      console.error("Error en coordenadas de destino:", error);
      return null;
    }
  }, [trip?.destination]);

  // Configuración de estilos para rutas
  const googlePathOptions = {
    strokeColor: routeColor,
    strokeOpacity: 0.8,
    strokeWeight: isSelected ? 4 : 3,
    zIndex: isSelected ? 100 : 10,
  };

  const actualPathOptions = {
    strokeColor: routeColor,
    strokeOpacity: 0.6,
    strokeWeight: isSelected ? 3 : 2,
    strokePattern: [10, 5], // Línea punteada
    zIndex: isSelected ? 90 : 5,
  };
  
  const getStartMarkerIcon = () => ({
    url: IconStar,
    scaledSize: new window.google.maps.Size(32, 32),
    anchor: new window.google.maps.Point(16, 16)
  });
  
  const getEndMarkerIcon = () => ({
    url: IconEnd,
    scaledSize: new window.google.maps.Size(32, 32),
    anchor: new window.google.maps.Point(16, 16)
  });
  
  if (!trip) return null;

  return (
    <>
      {/* Ruta de Google Maps (óptima) */}
      {decodedGooglePath.length > 0 && (
        <Polyline
          path={decodedGooglePath}
          options={googlePathOptions}
        />
      )}

      {/* Ruta real recorrida (GPS) */}
      {actualLatLngPath.length > 0 && (
        <Polyline
          path={actualLatLngPath}
          options={actualPathOptions}
        />
      )}

      {/* Marcadores de origen y destino con iconos personalizados */}
      {originMarker && (
        <Marker
          position={originMarker}
          icon={getStartMarkerIcon()}
          title={`Origen: ${trip.routeName || 'Sin nombre'}`}
          zIndex={isSelected ? 110 : 20}
        />
      )}

      {destinationMarker && (
        <Marker
          position={destinationMarker}
          icon={getEndMarkerIcon()}
          title={`Destino: ${trip.routeName || 'Sin nombre'}`}
          zIndex={isSelected ? 110 : 20}
        />
      )}
    </>
  );
};
RouteRenderer.propTypes = {
  trip: PropTypes.shape({
    vehiclePlate: PropTypes.string,
    routeName: PropTypes.string,
    origin: PropTypes.shape({
      lat: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      lon: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      dateTime: PropTypes.string
    }),
    destination: PropTypes.shape({
      lat: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      lon: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      dateTime: PropTypes.string
    })
  }).isRequired,
  googleMapsPolyline: PropTypes.string,
  actualPath: PropTypes.arrayOf(
    PropTypes.shape({
      latitud: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      longitud: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    })
  ),
  routeColor: PropTypes.string,
  isSelected: PropTypes.bool
};

export default RouteRenderer;